import {
  isTitleExtractCopywritingResult,
  localizeCopywritingLanguageLabel,
  parseCopywritingVariants,
  resolveCopywritingLanguageInfo,
  sanitizeCopywritingContent,
} from '@/pages/ipe/workbench/constants/aiCopywritingConfig';
import i18n from '@/i18n';
import { stripImageFileExtension } from '@/utils/imageUrlUtils';

function resolveReferencePreviews(result) {
  const r = result && typeof result === 'object' ? result : {};
  const previews = Array.isArray(r.referencePreviews) ? r.referencePreviews : [];
  const urls = Array.isArray(r.referenceUrls) ? r.referenceUrls : [];
  const count = Math.max(previews.length, urls.length);
  const list = [];
  for (let i = 0; i < count; i += 1) {
    const preview = String(previews[i] || urls[i] || '').trim();
    const apiUrl = String(urls[i] || previews[i] || '').trim();
    if (!preview && !apiUrl) continue;
    list.push(preview || apiUrl);
  }
  return list;
}

function fileNameFromUrl(url) {
  const raw = String(url || '').trim();
  if (!raw) return '';
  try {
    const path = raw.split('?')[0].split('#')[0];
    const seg = path.substring(path.lastIndexOf('/') + 1);
    return stripImageFileExtension(decodeURIComponent(seg || '') || '');
  } catch {
    return '';
  }
}

export function normalizeStoredImageGroups(result) {
  const r = result && typeof result === 'object' ? result : {};
  if (!Array.isArray(r.imageGroups) || r.imageGroups.length === 0) return [];
  return r.imageGroups.map((group) => {
    const urls = Array.isArray(group?.urls) ? group.urls : [];
    const previews = Array.isArray(group?.previews) ? group.previews : [];
    const names = Array.isArray(group?.names) ? group.names : [];
    const count = Math.max(urls.length, previews.length);
    const images = [];
    for (let i = 0; i < count; i += 1) {
      const preview = String(previews[i] || urls[i] || '').trim();
      const apiUrl = String(urls[i] || previews[i] || '').trim();
      if (!preview && !apiUrl) continue;
      const name = stripImageFileExtension(names[i] || '')
        || fileNameFromUrl(apiUrl || preview)
        || i18n.t('workbench:detailChips.nameImageNth', { index: i + 1 });
      images.push({
        previewUrl: preview || apiUrl,
        apiUrl: apiUrl || preview,
        name,
      });
    }
    return images;
  }).filter((images) => images.length > 0);
}

export function isCustomGroupCopywritingResult(result) {
  return normalizeStoredImageGroups(result).length > 0;
}

function buildTextOutput(text, title = '') {
  const body = String(text || '').trim();
  if (!body) return null;
  return {
    status: 'success',
    text: body,
    content: body,
    title: title || '',
  };
}

function splitContentBlocks(content, refCount) {
  const blocks = String(content || '')
    .split(/\n\n+/)
    .map((part) => part.trim())
    .filter(Boolean);
  if (blocks.length === 0) return [];
  if (refCount <= 1) return blocks;
  if (blocks.length === refCount) return blocks;
  if (blocks.length === 1) return blocks;
  return blocks;
}

/** 解析 AI 文案任务 result，供详情页左图右文展示（含历史 titleExtract 只读兼容） */
export function buildCopywritingDetailGroups(result, total) {
  const r = result && typeof result === 'object' ? result : {};
  const refs = resolveReferencePreviews(r);
  const storedGroups = normalizeStoredImageGroups(r);
  const refCount = storedGroups.length > 0 ? storedGroups.length : refs.length;
  const rawContent = String(r.content || r.text || '').trim();
  // 历史 AI文案·标题提取任务：不剥 Markdown；新任务一律按图生文案清理
  const isLegacyTitleExtract = isTitleExtractCopywritingResult(r);
  const displayContent = isLegacyTitleExtract
    ? rawContent
    : sanitizeCopywritingContent(rawContent);
  const perOutputCount = Math.max(1, Number(r.outputCount) || 1);

  if (!displayContent && refCount === 0) {
    return [{
      groupIndex: 0,
      referencePreview: '',
      referenceImages: [],
      extraCount: 0,
      outputs: [{ index: 0, output: null }],
    }];
  }

  // 自定义分组：每组仅首图提取标题，详情保留组内全部图片
  if (isCustomGroupCopywritingResult(r) && storedGroups.length > 0) {
    const blocks = splitContentBlocks(displayContent, storedGroups.length);
    return storedGroups.map((images, ri) => {
      const block = blocks[ri] || (blocks.length === 1 ? blocks[0] : '');
      const referenceImageItems = images.map((img) => ({
        url: img.apiUrl || img.previewUrl,
        previewUrl: img.previewUrl || img.apiUrl,
        name: img.name || '',
      }));
      const referenceImages = referenceImageItems
        .map((item) => item.previewUrl || item.url)
        .filter(Boolean);
      return {
        groupIndex: ri,
        referencePreview: referenceImages[0] || '',
        referenceImages,
        referenceImageItems,
        extraCount: Math.max(0, referenceImages.length - 1),
        outputs: [{
          index: ri,
          output: buildTextOutput(block),
        }],
      };
    });
  }

  // 历史：多商品图标题提取 → 每张图一组
  if (isLegacyTitleExtract && refCount > 0) {
    const blocks = splitContentBlocks(displayContent, refCount);
    return refs.map((referencePreview, ri) => {
      const block = blocks[ri] || (blocks.length === 1 ? blocks[0] : '');
      const variants = parseCopywritingVariants(block, perOutputCount, { sanitize: false });
      const outputs = (variants.length > 0
      ? variants
      : [{ index: 1, title: i18n.t('workbench:detailChips.copyTitleFallback'), body: block }])
        .map((variant, qi) => ({
          index: ri * perOutputCount + qi,
          output: buildTextOutput(variant.body, variant.title),
        }));
      return {
        groupIndex: ri,
        referencePreview,
        referenceImages: referencePreview ? [referencePreview] : [],
        extraCount: 0,
        outputs: outputs.length > 0 ? outputs : [{ index: ri, output: buildTextOutput(block) }],
      };
    });
  }

  if (refCount > 0) {
    const blocks = splitContentBlocks(displayContent, refCount);
    if (blocks.length === refCount && refCount > 1) {
      return refs.map((referencePreview, ri) => ({
        groupIndex: ri,
        referencePreview,
        referenceImages: referencePreview ? [referencePreview] : [],
        extraCount: 0,
        outputs: [{
          index: ri,
          output: buildTextOutput(blocks[ri]),
        }],
      }));
    }

    // 新任务默认 1 条；历史多条仍按 outputCount 拆方案
    const variants = parseCopywritingVariants(displayContent, perOutputCount, { sanitize: false });
    const outputs = (variants.length > 0
      ? variants
      : [{ index: 1, title: i18n.t('workbench:detailChips.copyBodyFallback'), body: displayContent }])
      .map((variant, qi) => ({
        index: qi,
        output: buildTextOutput(variant.body, variant.title),
      }));

    return [{
      groupIndex: 0,
      referencePreview: refs[0],
      referenceImages: refs[0] ? [refs[0]] : [],
      extraCount: 0,
      outputs,
    }];
  }

  const variants = parseCopywritingVariants(displayContent, perOutputCount, { sanitize: false });
  const outputs = (variants.length > 0
    ? variants
    : [{ index: 1, title: i18n.t('workbench:detailChips.copyBodyFallback'), body: displayContent }])
    .map((variant, qi) => ({
      index: qi,
      output: buildTextOutput(variant.body, variant.title),
    }));

  const limit = Math.max(1, Number(total) || outputs.length || 1);
  return [{
    groupIndex: 0,
    referencePreview: '',
    referenceImages: [],
    extraCount: 0,
    outputs: outputs.slice(0, limit),
  }];
}

export function getCopywritingOutputCount(result, taskTotal) {
  const groups = buildCopywritingDetailGroups(result, taskTotal);
  const count = groups.reduce((sum, group) => sum + (group.outputs?.length || 0), 0);
  const successCount = groups.reduce(
    (sum, group) => sum + (group.outputs || []).filter((item) => item.output?.text).length,
    0,
  );
  const totalRaw = Number(taskTotal);
  if (Number.isFinite(totalRaw) && totalRaw > 0) {
    return Math.max(totalRaw, successCount || count || 1);
  }
  return Math.max(successCount, count, 1);
}

export function resolveCopywritingPromptText(result) {
  const r = result && typeof result === 'object' ? result : {};
  // 新任务用 prompt；模板任务可能只落了 titlePresetContent；历史标题提取可能只有 titleTemplate
  return String(r.prompt || r.titlePresetContent || r.titleTemplate || '').trim();
}

export function buildCopywritingParamsChips(result) {
  const r = result && typeof result === 'object' ? result : {};
  const chips = [];
  const { label: languageLabel } = resolveCopywritingLanguageInfo(r);
  if (languageLabel) {
    chips.push({
      label: i18n.t('workbench:detailChips.chipOutputLanguage'),
      value: localizeCopywritingLanguageLabel(languageLabel),
    });
  }
  const maxChars = Number(r.maxChars);
  if (Number.isFinite(maxChars) && maxChars > 0) {
    chips.push({
      label: i18n.t('workbench:detailChips.chipMaxChars'),
      value: i18n.t('workbench:detailChips.valMaxChars', { count: Math.floor(maxChars) }),
    });
  }
  const outputCount = Number(r.outputCount);
  // 新任务固定 1 条，不展示冗余芯片；历史多条仍展示
  if (Number.isFinite(outputCount) && outputCount > 1) {
    chips.push({ label: i18n.t('workbench:detailChips.chipCopyCount'), value: String(outputCount) });
  }
  if (isTitleExtractCopywritingResult(r)) {
    chips.push({
      label: i18n.t('workbench:detailChips.chipMode'),
      value: i18n.t('workbench:detailChips.valTitleExtractHist'),
    });
  } else if (isCustomGroupCopywritingResult(r)) {
    chips.push({
      label: i18n.t('workbench:detailChips.chipMode'),
      value: i18n.t('workbench:detailChips.valCustomGroup'),
    });
  } else {
    chips.push({
      label: i18n.t('workbench:detailChips.chipMode'),
      value: i18n.t('workbench:detailChips.valImageToCopy'),
    });
  }
  return chips;
}

export function buildCopywritingModeSubtitle({ refCount, total, result }) {
  const { label: languageLabel } = resolveCopywritingLanguageInfo(result || {});
  const outputCount = Number(result?.outputCount) || total || 1;
  const customGroups = normalizeStoredImageGroups(result);
  let mode;
  if (customGroups.length > 0) {
    const imageTotal = customGroups.reduce((sum, images) => sum + images.length, 0);
    mode = i18n.t('workbench:detailChips.subCopyGroup', {
      groups: customGroups.length,
      images: imageTotal,
    });
  } else if (refCount > 0) {
    mode = outputCount > 1
      ? i18n.t('workbench:detailChips.subCopyImageMulti', { refs: refCount, count: outputCount })
      : i18n.t('workbench:detailChips.subCopyImage', { refs: refCount });
  } else {
    mode = outputCount > 1
      ? i18n.t('workbench:detailChips.subCopyTextMulti', { count: outputCount })
      : i18n.t('workbench:detailChips.subCopyText');
  }
  return languageLabel
    ? `${mode} · ${localizeCopywritingLanguageLabel(languageLabel)}`
    : mode;
}
